// Territory: the map of New York, businesses in each borough, and the war for control.
import { useEffect, useState } from 'react';
import { api } from '../api.ts';
import { useGame, money, fmt, duration, ago } from '../state.tsx';
import { Loading, useLoad, Panel, PlayerLink, go } from '../components/ui.tsx';
import { PropertyArt, StatIcon } from '../art/Scenes.tsx';
import { Portrait } from '../art/Portrait.tsx';
import { NycMap, UnitIcon, StatueIcon } from '../art/NycMap.tsx';
import { BattleReplay, type Replay } from '../art/BattleReplay.tsx';
import { play, sequence } from '../fx/sound.ts';
import { fx } from '../fx/fx.tsx';

function readSelected(): string {
  try {
    return localStorage.getItem('mm_borough') ?? 'staten_island';
  } catch {
    return 'staten_island';
  }
}

/** Stable colour per player for the map. */
function ownerColor(id: number): string {
  const hues = [0, 25, 200, 140, 280, 320, 45, 170];
  return `hsl(${hues[id % hues.length]}, 45%, 32%)`;
}

export function TerritoryPage({ tab = 'map' }: { tab?: string }) {
  return (
    <div>
      <h1 className="page-title">Territory</h1>
      <div className="subnav" style={{ marginBottom: 8 }}>
        <a className={tab !== 'army' ? 'active' : ''} href="#/territory">Map & Businesses</a>
        <a className={tab === 'army' ? 'active' : ''} href="#/territory/army">My Army</a>
      </div>
      {tab === 'army' ? <Army /> : <MapView focus={tab?.startsWith('siege-') ? tab.slice(6) : undefined} battle={tab?.startsWith('battle-') ? Number(tab.slice(7)) : undefined} />}
    </div>
  );
}

function MapView({ focus, battle }: { focus?: string; battle?: number }) {
  const { me, act, content, setFlash, byId } = useGame();
  const siegeKey = (me?.sieges ?? []).map((x: any) => x.id).join(',');
  const { data, reload } = useLoad(() => api.get('/api/territory'), [me?.cash, siegeKey], 15_000);
  const props = useLoad(() => api.get('/api/properties'), [me?.cash]);
  const [selected, setSelected] = useState(() => focus ?? readSelected());
  useEffect(() => { if (focus) setSelected(focus); }, [focus]);
  const [send, setSend] = useState<Record<string, string>>({});
  const [qty, setQty] = useState<Record<string, number>>({});
  const [attacking, setAttacking] = useState<string | null>(null);
  const [captured, setCaptured] = useState<string | null>(null);
  // v0.9: the battle replay on top of the map (after an attack, or from the battle list / news).
  const [replay, setReplay] = useState<Replay | null>(null);
  const [afterReplay, setAfterReplay] = useState<(() => void) | null>(null);
  const watch = async (id: number) => {
    try { setReplay(await api.get(`/api/territory/battles/${id}`)); } catch (e) { setFlash({ kind: 'bad', text: (e as Error).message }); }
  };
  useEffect(() => { if (battle) watch(battle); }, [battle]);
  if (!data || !props.data) return <Loading />;
  const r = content.rules;
  const defs: Map<string, any> = new Map(content.boroughs.map((b: any) => [b.id, b]));
  const unitDefs: Map<string, any> = new Map(content.units.map((u: any) => [u.id, u]));
  const select = (id: string) => {
    setSelected(id);
    setSend({});
    try {
      localStorage.setItem('mm_borough', id);
    } catch { /* private mode */ }
  };
  const b = data.boroughs.find((x: any) => x.boroughId === selected) ?? data.boroughs[0];
  const def = defs.get(b.boroughId);
  const protectedNow = b.protectedUntil > Date.now();
  const sendUnits: Record<string, number> = {};
  for (const [id, v] of Object.entries(send)) {
    const n = Math.floor(Number(v));
    if (n > 0) sendUnits[id] = Math.min(n, data.reserve[id] ?? 0);
  }
  const sendPower = Object.entries(sendUnits).reduce((s, [id, n]) => s + unitDefs.get(id).attack * n, 0);
  const luck = r.territory.luckPct / 100;
  const odds = sendPower <= 0 ? null : sendPower * (1 - luck) > b.defense * (1 + luck) ? 'Sure thing' : sendPower * (1 + luck) < b.defense * (1 - luck) ? 'No chance' : sendPower > b.defense ? 'Good odds' : 'Long shot';

  const refresh = () => {
    reload();
    props.reload();
  };
  const attack = async () => {
    setAttacking(b.boroughId);
    // A siege (the borough has an owner): war drums while the crews dig in. A battle against the gangs plays out as a replay.
    if (b.owner) play('drum');
    const [res] = await Promise.all([act('/api/territory/attack', { boroughId: b.boroughId, units: sendUnits }), new Promise((ok) => setTimeout(ok, b.owner ? 1300 : 0))]);
    setAttacking(null);
    if (!res) return;
    if (res.siege) {
      sequence([[0, 'swords'], [500, 'siren']]);
      fx.stamp('Siege laid', `${def.name} · battle in ${r.territory.siegeMinutes} min`, 'bad');
      setFlash({
        kind: 'good',
        title: `You laid siege to ${def.name}!`,
        text: <>Your crews have surrounded the borough. The battle is fought in <b>{r.territory.siegeMinutes} minutes</b>, so {b.owner?.name ?? 'the owner'} has time to reinforce it or call in friends. Your units are tied up until then.</>,
      });
      setSend({});
      refresh();
      return;
    }
    const lost = Object.entries(res.attackerLosses).filter(([, n]: any) => n > 0).map(([id, n]: any) => `${n} ${unitDefs.get(id).name}`).join(', ') || 'nobody';
    const report = () => {
      if (res.won) { setCaptured(b.boroughId); setTimeout(() => setCaptured(null), 2500); }
      setFlash({
        kind: res.won ? 'good' : 'bad',
        title: res.won ? `You took ${def.name}!` : `The attack on ${def.name} failed.`,
        text: <>Your attack {fmt(res.attackPower)} vs. their defense {fmt(res.defensePower)}. You lost {lost}.{res.won && <> Your surviving units now hold the borough. It’s protected for {r.territory.captureProtectionHours} hours.</>}
          {res.battleId && <> <a onClick={() => watch(res.battleId)}>Watch it again</a></>}</>,
      });
    };
    setSend({});
    refresh();
    // Play the battle out on the map, then show the report.
    if (res.replay) { setReplay(res.replay); setAfterReplay(() => report); } else report();
  };
  const moveUnits = async (path: string, unitId: string, n: number) => {
    if (n <= 0) return;
    await act(path, { boroughId: b.boroughId, unitId, qty: n });
    refresh();
  };
  const buy = async (p: any) => {
    const n = qty[p.propertyId] ?? 1;
    await act('/api/properties/buy', { propertyId: p.propertyId, qty: n }, { success: (res) => ({ kind: 'good', text: <>You bought {n} {byId.properties.get(p.propertyId).name}{n > 1 ? 's' : ''} for <span className="money">{money(res.cost)}</span>.</> }) });
    refresh();
  };
  const sell = async (p: any) => {
    if (!confirm(`Sell one ${byId.properties.get(p.propertyId).name} for ${r.propertySellBackPct}% of its base price?`)) return;
    await act('/api/properties/sell', { propertyId: p.propertyId, qty: 1 }, { success: (res) => ({ kind: 'good', text: <>Sold for <span className="money">{money(res.value)}</span>.</> }) });
    refresh();
  };
  const businesses = props.data.properties.filter((p: any) => byId.properties.get(p.propertyId)?.borough === b.boroughId);
  const waiting = props.data.pending + props.data.tribute;

  return (
    <div>
      {replay && <BattleReplay replay={replay} onClose={() => { setReplay(null); if (afterReplay) { afterReplay(); setAfterReplay(null); } }} />}
      <div className="panel">
        <div className="panel-body row" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="grow">
            Your businesses earn <span className="money">{money(props.data.hourlyIncome)}</span> an hour, even while you’re offline.
            {data.boroughs.some((x: any) => x.mine) && <> Your boroughs pay you <span className="money">{money(data.boroughs.filter((x: any) => x.mine).reduce((s: number, x: any) => s + x.tributeRate, 0))}</span> an hour in tribute.</>}
            <div className="small muted">Income piles up for up to {props.data.capHours} hours, and robbers can only take what you haven’t collected. Illegal businesses earn more but only half during a Police Crackdown.</div>
          </div>
          <div className="right">
            <div>Waiting: <span className="money" style={{ fontSize: 14 }}>{money(waiting)}</span>{props.data.tribute > 0 && <div className="small dim">incl. {money(props.data.tribute)} tribute</div>}</div>
            <button className={`btn green ${waiting ? '' : 'disabled'}`} onClick={async () => { await act('/api/properties/collect', {}, { success: (x) => ({ kind: 'good', text: <>You collected <span className="money">{money(x.amount)}</span>{x.tribute ? <> (including {money(x.tribute)} tribute)</> : null}.</> }) }); refresh(); }}>Collect</button>
          </div>
        </div>
      </div>

      <div className="row territory-top">
        <div className="map-wrap">
          <NycMap
            attacking={attacking}
            captured={captured}
            statue={data.statue}
            selected={b.boroughId}
            onSelect={select}
            boroughs={data.boroughs.map((x: any) => ({
              id: x.boroughId,
              name: defs.get(x.boroughId).name,
              fill: x.mine ? '#8a6a10' : x.owner ? ownerColor(x.owner.id) : '#3a3a3a',
              ownerLabel: x.mine ? 'YOURS' : x.owner ? x.owner.name : 'Local gangs',
              mine: x.mine,
              siege: !!x.siege,
              shield: x.protectedUntil > Date.now(),
            }))}
          />
          <div className="small dim center">Click a borough. <span className="gold">Gold</span> = yours, grey = held by the local gangs, colors = other bosses.</div>
        </div>
        <div className="grow borough-panel">
          <h2 style={{ marginTop: 0 }}>{def.name} <span className="small dim">· {def.nickname}</span></h2>
          <div className="small muted" style={{ marginBottom: 6 }}>{def.description}</div>
          <div className="borough-owner">
            {b.owner ? (
              <>
                <Portrait seed={b.owner.portrait} playerId={b.owner.id} size={40} />
                <div>
                  <div>Controlled by {b.mine ? <b className="gold">you</b> : <PlayerLink id={b.owner.id} name={b.owner.name} />} <span className="small dim">L{b.owner.level}</span></div>
                  <div className="small dim">since {ago(b.capturedAt)}</div>
                </div>
              </>
            ) : <div>Held by the <b>local gangs</b>. Beat them to take control.</div>}
          </div>
          <div className="stat-grid" style={{ gridTemplateColumns: '1fr', margin: '6px 0' }}>
            <div><span><StatIcon kind="defense" /> Defense</span><b>{fmt(b.defense)}</b></div>
            <div><span>Units stationed</span><b>{fmt(b.garrisonSize)}</b></div>
            <div><span>Tribute to the boss</span><b className="money">{money(b.tributeRate)}/hr</b></div>
            <div><span>Your businesses here</span><b className="money">{money(b.myIncome)}/hr</b></div>
          </div>
          {protectedNow && <div className="small gold"><span className="locked-stamp">Protected</span> for {duration(b.protectedUntil - Date.now())} after changing hands.</div>}

          {b.siege && <SiegePanel siege={b.siege} borough={{ ...def, ownerId: b.owner?.id ?? null }} mine={b.mine} reserve={data.reserve} onChange={refresh} />}

          {b.siege && !b.mine ? null : b.mine ? (
            <Panel title="Your garrison">
              {content.units.map((u: any) => {
                const here = b.garrison?.[u.id] ?? 0;
                const spare = data.reserve[u.id] ?? 0;
                if (!here && !spare) return null;
                return (
                  <div key={u.id} className="feed-item" style={{ alignItems: 'center' }}>
                    <UnitIcon unit={u.id} size={28} />
                    <span className="grow">{u.name}: <b>{here}</b> here · {spare} in reserve</span>
                    {spare > 0 && <a className="small" onClick={() => moveUnits('/api/territory/station', u.id, spare)}>station all</a>}
                    {here > 0 && <a className="small" style={{ marginLeft: 6 }} onClick={() => moveUnits('/api/territory/withdraw', u.id, here)}>withdraw</a>}
                  </div>
                );
              })}
              <div className="small dim">Stationed units defend {r.territory.defenderBonusPct}% better. Defensive units (Bodyguards, Armored Cars) are best here.</div>
            </Panel>
          ) : (
            <Panel title={`Attack ${def.name}`}>
              {Object.keys(data.reserve).length === 0 ? <div className="muted">You have no units in reserve. <a onClick={() => go('territory/army')}>Recruit some</a>.</div> : (
                <>
                  {content.units.filter((u: any) => data.reserve[u.id]).map((u: any) => (
                    <div key={u.id} className="feed-item" style={{ alignItems: 'center' }}>
                      <UnitIcon unit={u.id} size={28} />
                      <span className="grow">{u.name} <span className="small dim">atk {u.attack}</span></span>
                      <input value={send[u.id] ?? ''} placeholder="0" onChange={(e) => setSend({ ...send, [u.id]: e.target.value })} style={{ width: 56 }} />
                      <a className="small" style={{ marginLeft: 4 }} onClick={() => setSend({ ...send, [u.id]: String(data.reserve[u.id]) })}>all {data.reserve[u.id]}</a>
                    </div>
                  ))}
                  <div style={{ margin: '6px 0' }}>
                    Your attack <b>{fmt(sendPower)}</b> vs. defense <b>{fmt(b.defense)}</b>
                    {odds && <span className={odds === 'Sure thing' || odds === 'Good odds' ? 'good' : 'bad'}> · {odds}</span>}
                    <div className="small dim">Each side’s strength varies ±{r.territory.luckPct}%. The loser loses {r.territory.loserLossMinPct}–{r.territory.loserLossMaxPct}% of its units, the winner up to {r.territory.winnerLossMaxPct}%. Costs {r.territory.attackStaminaCost} stamina.</div>
                  </div>
                  <button className={`btn red big ${sendPower > 0 && !protectedNow && me.stamina >= r.territory.attackStaminaCost && b.canAttackAt <= Date.now() ? '' : 'disabled'}`} onClick={attack}>Attack!</button>
                  {b.canAttackAt > Date.now() && <span className="small dim"> You can hit {def.name} again in {duration(b.canAttackAt - Date.now())}.</span>}
                </>
              )}
            </Panel>
          )}
        </div>
      </div>

      <Panel title={`Businesses in ${def.name}`}>
        <table className="list stack">
          <thead><tr><th></th><th>Business</th><th>Income / hr</th><th>Owned</th><th>Price</th><th></th></tr></thead>
          <tbody>
            {businesses.map((p: any) => {
              const d = byId.properties.get(p.propertyId);
              const n = qty[p.propertyId] ?? 1;
              return (
                <tr key={p.propertyId} className={p.locked ? 'locked' : ''}>
                  <td style={{ width: 62 }}><PropertyArt icon={d.icon} propertyId={d.id} /></td>
                  <td>
                    <div className="job-name">{d.name} {d.illegal ? <span className="tag illegal-tag" title="Earns more, but half during a Police Crackdown">ILLEGAL</span> : <span className="tag legal-tag">legal</span>}</div>
                    <div className="small muted">{d.description}</div>
                    {p.locked && <div className="small"><span className="locked-stamp">Locked</span> until level {d.reqLevel}</div>}
                  </td>
                  <td className="money">{money(d.income)}</td>
                  <td>{fmt(p.owned)}{p.owned > 0 && <div><a className="small" onClick={() => sell(p)}>sell</a></div>}</td>
                  <td className="money">{money(p.nextPrice)}</td>
                  <td className="right" style={{ whiteSpace: 'nowrap' }}>
                    <select value={n} onChange={(e) => setQty({ ...qty, [p.propertyId]: Number(e.target.value) })} disabled={p.locked}>
                      {[1, 5, 10].map((x) => <option key={x} value={x}>{x}</option>)}
                    </select>{' '}
                    <button className={`btn ${p.locked ? 'disabled' : ''}`} onClick={() => buy(p)}>Buy</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="small dim">Each copy you own raises the next price by {r.propertyPriceIncreasePct}%. Whoever controls {def.name} collects {r.territory.tributePct}% of everyone else’s income here (paid by the city, not taken from you).</div>
      </Panel>

      <StatuePanel />

      <Panel title="Turf wars">
        {data.battles.length === 0 ? <div className="muted">No battles yet. The city is quiet… for now.</div> : data.battles.map((x: any) => (
          <div key={x.id} className="feed-item">
            <span className="grow">
              <PlayerLink id={x.attackerId} name={x.attackerName} /> {x.won ? <b className="good">took</b> : <span className="bad">failed to take</span>} {defs.get(x.boroughId).name}
              {' '}from {x.defenderId ? <PlayerLink id={x.defenderId} name={x.defenderName} /> : 'the local gangs'}
              <span className="small dim"> ({fmt(x.attackPower)} vs {fmt(x.defensePower)})</span>
              {x.hasReplay ? <> <a className="feed-go" onClick={() => watch(x.id)}>Watch ›</a></> : null}
            </span>
            <span className="when">{ago(x.createdAt)}</span>
          </div>
        ))}
      </Panel>
    </div>
  );
}

/** A borough under siege: the countdown, both sides' strength, and help from friends. */
function SiegePanel({ siege, borough, mine, reserve, onChange }: { siege: any; borough: any; mine: boolean; reserve: Record<string, number>; onChange: () => void }) {
  const { me, act, content } = useGame();
  const [, tick] = useState(0);
  const [picking, setPicking] = useState(false);
  const [send, setSend] = useState<Record<string, string>>({});
  const people = useLoad(() => (picking ? api.get('/api/chat/people') : Promise.resolve(null)), [picking]);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const left = Math.max(0, siege.endsAt - Date.now());
  const total = Math.max(1, siege.endsAt - siege.startedAt);
  const mm = `${Math.floor(left / 60000)}:${String(Math.floor(left / 1000) % 60).padStart(2, '0')}`;
  const ahead = siege.defenseNow >= siege.attackStrength;
  const iAttack = siege.attackerId === me.id;
  const invite = async (id: number, name: string) => {
    if (await act('/api/territory/siege/invite', { siegeId: siege.id, playerId: id })) {
      play('envelope');
      fx.float(`Asked ${name}`, 'gold');
      onChange();
    }
  };
  const units: Record<string, number> = {};
  for (const [id, v] of Object.entries(send)) {
    const n = Math.floor(Number(v));
    if (n > 0) units[id] = Math.min(n, reserve[id] ?? 0);
  }
  const sendHelp = async () => {
    const res = await act('/api/territory/siege/send', { siegeId: siege.id, units });
    if (res) {
      sequence([[0, 'drum'], [600, 'horn']]);
      fx.stamp('Reinforcements sent', `${res.sent} units to ${borough.name}`, 'good');
      setSend({});
      onChange();
    }
  };
  const reinforce = async () => {
    const res = await act('/api/territory/siege/reinforce', { siegeId: siege.id, units });
    if (res) {
      sequence([[0, 'drum'], [500, 'swords']]);
      fx.stamp('More men', `${res.added} units join the siege`, 'bad');
      setSend({});
      onChange();
    }
  };
  const asked = new Set(siege.helpers.map((h: any) => h.id));
  const atkHelpers = siege.helpers.filter((h: any) => h.side === 'attacker');
  const defHelpers = siege.helpers.filter((h: any) => h.side !== 'attacker');
  // Who is this for? The attacker adds their own units; anyone invited sends units to their side.
  const mySide: 'attacker' | 'defender' | null = iAttack ? 'attacker' : mine ? 'defender' : siege.myHelperSide;
  const canSend = (iAttack && siege.canReinforce) || (siege.iAmHelper && !mine);
  const backup = (list: any[]) => list.map((h: any, i: number) => <span key={h.id}>{i > 0 && ', '}<PlayerLink id={h.id} name={h.name} /> {h.sent ? <b className="good">({h.units} units)</b> : <span className="dim">(asked)</span>}</span>);
  return (
    <div className="siege-panel">
      <div className="siege-head">
        <span className="siege-tag">Under siege</span>
        <span className="grow" />
        <span className="siege-clock">{left > 0 ? mm : 'Fighting…'}</span>
      </div>
      <div style={{ marginTop: 4 }}><PlayerLink id={siege.attackerId} name={siege.attackerName} /> has {borough.name} surrounded.</div>
      <div className="siege-fuse"><div style={{ width: `${(1 - left / total) * 100}%` }} /></div>
      <div className="siege-sides">
        <div><div className="small">Attack</div><b className="bad">{fmt(siege.attackStrength)}</b></div>
        <div className="vs">vs</div>
        <div><div className="small">Defense now</div><b className={ahead ? 'good' : ''}>{fmt(siege.defenseNow)}</b></div>
      </div>
      <div className="small" style={{ marginBottom: 6 }}>
        {mySide === 'attacker'
          ? (ahead ? <>The defenders are stronger right now. Throw in more units or call in friends before the clock runs out.</> : <>You’re ahead for now, but each side swings ±{content.rules.territory.luckPct}% on the day.</>)
          : ahead ? <>You’re ahead for now, but each side swings ±{content.rules.territory.luckPct}% on the day.</> : <>The attackers are stronger. {mine ? 'Station more units here or call in friends.' : 'Send units to tip the balance.'}</>}
      </div>
      {atkHelpers.length > 0 && <div className="small" style={{ marginBottom: 4 }}><b className="bad">With the attackers:</b> {backup(atkHelpers)}</div>}
      {defHelpers.length > 0 && <div className="small" style={{ marginBottom: 6 }}><b className="good">With the defenders:</b> {backup(defHelpers)}</div>}
      {siege.canInvite && (
        picking ? (
          <div className="siege-invite">
            <div className="small"><b>Ask a friend {mySide === 'attacker' ? 'to join the siege' : 'for backup'}</b> (up to {content.rules.territory.maxHelpers}). They get an alert and can send units from their reserve.</div>
            {!people.data ? <Loading /> : people.data.players.filter((x: any) => x.id !== me.id && x.id !== siege.attackerId && x.id !== borough.ownerId).map((x: any) => (
              <div key={x.id} className="feed-item" style={{ alignItems: 'center' }}>
                <span className="grow">{x.name}</span>
                {asked.has(x.id) ? <span className="small dim">asked</span> : <a className="btn small" onClick={() => invite(x.id, x.name)}>{mySide === 'attacker' ? 'Bring them in' : 'Ask for help'}</a>}
              </div>
            ))}
            <a className="small" onClick={() => setPicking(false)}>close</a>
          </div>
        ) : <button className="btn small" onClick={() => setPicking(true)}>Call in friends</button>
      )}
      {canSend && (
        <div className="siege-invite">
          <div className="small">
            {iAttack ? <><b>Add units to the siege.</b> They join your crews outside {borough.name}. If you win, your survivors hold the borough.</>
              : mySide === 'attacker' ? <><b>Send units to join the attack.</b> If it falls, your survivors come home (the borough goes to {siege.attackerName}).</>
                : <><b>Send units to help.</b> They fight with the defenders’ bonus; the survivors come back to your reserve after the battle.</>}
          </div>
          {Object.keys(reserve).length === 0 ? <div className="muted small">You have no units in reserve. <a onClick={() => go('territory/army')}>Recruit some</a>.</div>
            : content.units.filter((u: any) => reserve[u.id]).map((u: any) => (
              <div key={u.id} className="feed-item" style={{ alignItems: 'center' }}>
                <UnitIcon unit={u.id} size={28} />
                <span className="grow">{u.name} <span className="small dim">{mySide === 'attacker' ? `atk ${u.attack}` : `def ${u.defense}`}</span></span>
                <input value={send[u.id] ?? ''} placeholder="0" aria-label={`How many ${u.name}`} onChange={(e) => setSend({ ...send, [u.id]: e.target.value })} style={{ width: 56 }} />
                <a className="small" style={{ marginLeft: 4 }} onClick={() => setSend({ ...send, [u.id]: String(reserve[u.id]) })}>all {reserve[u.id]}</a>
              </div>
            ))}
          {Object.keys(units).length > 0 && (iAttack
            ? <button className="btn red" onClick={reinforce}>Add to the siege</button>
            : <button className="btn green" onClick={sendHelp}>Send reinforcements</button>)}
        </div>
      )}
    </div>
  );
}

/** v0.8: the statue in the park, this month's race for the next one, and every statue so far. */
function StatuePanel() {
  const { me } = useGame();
  const { data } = useLoad(() => api.get('/api/statues'), [me?.level], 60_000);
  if (!data) return null;
  const s = data.current;
  return (
    <Panel title="Statues in the City" right={<span className="small dim">the most XP in a month earns a statue</span>}>
      <div className="row statue-row" style={{ flexWrap: 'wrap' }}>
        <div className="statue-card">
          <StatueIcon size={64} />
          {s ? <div><div className="gold"><b>{s.name}</b></div><div className="small">#1 for {s.monthName}</div><div className="small dim">{fmt(s.xpGained)} XP that month</div></div>
            : <div className="small muted">No statue yet. The first one goes up when this month ends.</div>}
        </div>
        <div className="grow" style={{ minWidth: 220 }}>
          <div className="small" style={{ marginBottom: 4 }}><b>Race for {data.monthName}</b> · ends in {duration(Math.max(0, data.endsAt - Date.now()))}</div>
          {data.race.length === 0 ? <div className="muted small">No one has earned XP yet this month.</div> : data.race.slice(0, 5).map((r: any) => (
            <div key={r.id} className={`feed-item ${r.me ? 'me-line' : ''}`} style={{ alignItems: 'center' }}>
              <b style={{ width: 18 }}>{r.rank}</b>
              <Portrait seed={r.portrait} playerId={r.id} size={22} />
              <span className="grow"><PlayerLink id={r.id} name={r.name} /></span>
              <span className="small">{fmt(r.gained)} XP</span>
            </div>
          ))}
          {data.statues.length > 1 && <div className="small dim" style={{ marginTop: 4 }}>Earlier statues: {data.statues.slice(1).map((x: any) => `${x.name} (${x.monthName})`).join(', ')}</div>}
        </div>
      </div>
    </Panel>
  );
}

function Army() {
  const { me, act, content } = useGame();
  const { data, reload } = useLoad(() => api.get('/api/territory'), [me?.cash]);
  const [qty, setQty] = useState<Record<string, string>>({});
  if (!data) return <Loading />;
  const r = content.rules.territory;
  const defs: Map<string, any> = new Map(content.boroughs.map((b: any) => [b.id, b]));
  const stationed: Record<string, number> = {};
  for (const b of data.boroughs) for (const [id, n] of Object.entries(b.garrison ?? {})) stationed[id] = (stationed[id] ?? 0) + (n as number);
  const buy = async (u: any) => {
    const n = Math.max(1, Math.floor(Number(qty[u.id] ?? 1)));
    await act('/api/territory/units/buy', { unitId: u.id, qty: n }, { success: (x) => ({ kind: 'good', text: <>Recruited {n} {u.name} for <span className="money">{money(x.cost)}</span>.</> }) });
    reload();
  };
  const dismiss = async (u: any) => {
    const n = data.reserve[u.id] ?? 0;
    if (!n || !confirm(`Dismiss all ${n} ${u.name} in reserve? You get ${r.dismissRefundPct}% of their price back.`)) return;
    await act('/api/territory/units/dismiss', { unitId: u.id, qty: n });
    reload();
  };
  return (
    <div>
      <p className="muted">
        Units fight for boroughs. Units in your <b>reserve</b> can attack; units <b>stationed</b> in a borough you control defend it. Your army can be
        {' '}{r.armyBase} + {r.armyPerLevel} per level strong: <b>{fmt(data.armySize)}</b> of <b>{fmt(data.armyCap)}</b> at your level.
      </p>
      <div className="row" style={{ gap: 16, marginBottom: 8, flexWrap: 'wrap' }}>
        <span>Reserve attack power: <b>{fmt(data.attackPower)}</b></span>
        <span>Boroughs held: <b>{data.boroughs.filter((b: any) => b.mine).map((b: any) => defs.get(b.boroughId).name).join(', ') || 'none'}</b></span>
      </div>
      <table className="list stack">
        <thead><tr><th></th><th>Unit</th><th>Attack</th><th>Defense</th><th>Reserve</th><th>Stationed</th><th>Price</th><th></th></tr></thead>
        <tbody>
          {content.units.map((u: any) => {
            const locked = me.level < u.reqLevel;
            return (
              <tr key={u.id} className={locked ? 'locked' : ''}>
                <td style={{ width: 50 }}><UnitIcon unit={u.id} size={44} /></td>
                <td><div className="job-name">{u.name}</div><div className="small muted">{u.description}</div>{locked && <div className="small"><span className="locked-stamp">Locked</span> until level {u.reqLevel}</div>}</td>
                <td>{u.attack}</td>
                <td>{u.defense}</td>
                <td>{fmt(data.reserve[u.id] ?? 0)}{(data.reserve[u.id] ?? 0) > 0 && <div><a className="small" onClick={() => dismiss(u)}>dismiss</a></div>}</td>
                <td>{fmt(stationed[u.id] ?? 0)}</td>
                <td className="money">{money(u.price)}</td>
                <td className="right" style={{ whiteSpace: 'nowrap' }}>
                  <input value={qty[u.id] ?? '1'} onChange={(e) => setQty({ ...qty, [u.id]: e.target.value })} style={{ width: 48 }} disabled={locked} />{' '}
                  <button className={`btn ${locked ? 'disabled' : ''}`} onClick={() => buy(u)}>Recruit</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="small dim">Shooters and Hit Squads are best on the attack; Bodyguards and Armored Cars are best at holding a borough.</div>
    </div>
  );
}
